/**
 * Recherche de l'en-tête sur téléphone (option A « sobre », validée par Alexis le 29/10) :
 * - fermer le clavier (✓) ferme aussi le panneau ; avec un texte non encore cherché, la
 *   recherche est lancée, comme avec Entrée. Toucher un autre élément (Trier, Filtrer, une
 *   pastille, le menu) ferme seulement : c'est cet élément que le client voulait ;
 * - après un choix ou Entrée, le clavier se ferme pour laisser voir les résultats ;
 * - un toucher sur une voiture, panneau ouvert, ferme le panneau sans ouvrir la fiche.
 * Le contenu du panneau à vide (recherches récentes seulement) est réglé dans
 * marketplace-header-autocomplete.ts.
 */

/** Même point de rupture que le CSS (`medium-down`, < 768 px). */
export const COMPACT_SEARCH_MEDIA = "(max-width: 47.9375rem)";

/** Laisse le toucher sur une suggestion aller au bout avant de juger la perte du focus. */
const BLUR_SETTLE_MS = 150;

/** Un toucher ailleurs dans ce délai explique la perte du focus : pas de recherche. */
const POINTER_BLUR_WINDOW_MS = 600;

/** Ce qu'un toucher, panneau ouvert, ne doit pas ouvrir : les cartes de voitures. */
const SWALLOWED_TARGETS = ".vehicle-card, .ais-Hits, .ais-InfiniteHits";

export function isCompactSearch(): boolean {
    return typeof window.matchMedia === "function" && window.matchMedia(COMPACT_SEARCH_MEDIA).matches;
}

type CompactSearchOptions = {
    container: Element;
    /** Ferme le panneau de suggestions. */
    close: () => void;
    /** Lance la recherche du texte saisi (même chemin qu'Entrée). */
    commit: (query: string) => void;
    /** Dernier texte cherché, pour ne pas relancer la même recherche. */
    lastCommitted: () => string;
    isCompact?: () => boolean;
};

type Installed = Required<Pick<CompactSearchOptions, "container" | "isCompact">>;

/** Écouteurs de document installés une seule fois, partagés par toutes les recherches montées. */
const installed = new Set<Installed>();
let lastPointer = { at: -Infinity, target: null as EventTarget | null };
let swallowNextClick = false;
let decidedAt = -Infinity;
let documentListenersReady = false;

const isPanelOpen = (container: Element) => container.querySelector('[aria-expanded="true"]') !== null;

const isInsideSearch = (container: Element, target: EventTarget | null) =>
    target instanceof Node && (container.contains(target) || !!(target as Element).closest?.(".aa-Panel"));

function activeSearches(): Installed[] {
    return [...installed].filter((search) => search.container.isConnected !== false);
}

function installDocumentListeners(): void {
    if (documentListenersReady) return;
    documentListenersReady = true;

    // `pointerdown` arrive avant `touchstart` : d'autres écouteurs ferment déjà le panneau entre
    // les deux, d'où la décision prise au premier des deux.
    const decide = (event: Event) => {
        if (event.timeStamp - decidedAt < 50) return;
        decidedAt = event.timeStamp;
        lastPointer = { at: event.timeStamp, target: event.target };
        const target = event.target as Element | null;
        swallowNextClick =
            !!target?.closest?.(SWALLOWED_TARGETS) &&
            activeSearches().some(
                (search) => search.isCompact() && isPanelOpen(search.container) && !isInsideSearch(search.container, target),
            );
    };
    document.addEventListener("pointerdown", decide, { capture: true, passive: true });
    document.addEventListener("touchstart", decide, { capture: true, passive: true });
    document.addEventListener(
        "click",
        (event) => {
            if (!swallowNextClick) return;
            swallowNextClick = false;
            if (!(event.target as Element | null)?.closest?.(SWALLOWED_TARGETS)) return;
            event.preventDefault();
            event.stopPropagation();
        },
        { capture: true },
    );
}

export function installCompactSearchBehavior(options: CompactSearchOptions): {
    /** À appeler après un choix ou Entrée : ferme le clavier sur téléphone. */
    releaseFocus: () => void;
} {
    const { container, close, commit, lastCommitted } = options;
    const isCompact = options.isCompact ?? isCompactSearch;
    const input = container.querySelector<HTMLInputElement>(".aa-Input");

    // Un même conteneur remonté remplace l'ancienne entrée
    for (const search of installed) {
        if (search.container === container) installed.delete(search);
    }
    installed.add({ container, isCompact });
    installDocumentListeners();

    let tabbedAway = false;
    if (input) {
        input.addEventListener("keydown", (event) => {
            tabbedAway = event.key === "Tab";
        });
        input.addEventListener("blur", (event) => {
            if (!isCompact()) return;
            const blurredAt = event.timeStamp;
            window.setTimeout(() => {
                if (document.activeElement === input) return;
                close();
                const touchedElsewhere =
                    blurredAt - lastPointer.at < POINTER_BLUR_WINDOW_MS && !isInsideSearch(container, lastPointer.target);
                const query = input.value.trim();
                if (!touchedElsewhere && !tabbedAway && query !== "" && query !== lastCommitted().trim()) {
                    commit(query);
                }
                tabbedAway = false;
            }, BLUR_SETTLE_MS);
        });
    }

    return {
        releaseFocus: () => {
            if (isCompact() && input && document.activeElement === input) {
                input.blur();
            }
        },
    };
}

/** Tests : repart d'un état vierge. */
export function resetCompactSearchBehaviorForTests(): void {
    installed.clear();
    lastPointer = { at: -Infinity, target: null };
    swallowNextClick = false;
    decidedAt = -Infinity;
    documentListenersReady = false;
}
